"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { HotspotScore } from "@/lib/types";
import { copyText } from "@/lib/clipboard";
import { TokenDrawer } from "./TokenDrawer";
import { XTrends } from "./XTrends";

type Tab = "hotspots" | "xtrends";
type Filter = "all" | "cex-swap" | "onchain";
type Scale = "ultra" | "short" | "mid";

const FILTER_LABELS: Record<Filter, string> = {
  all: "全部",
  "cex-swap": "Binance 合約",
  onchain: "鏈上",
};

const SCALE_LABELS: Record<Scale, { short: string; hint: string }> = {
  ultra: { short: "極短線", hint: "4h 動能 · 60min 加速 · 通知用這個" },
  short: { short: "短線", hint: "12h 動能 · 24h 突破 · 3h 加速" },
  mid: { short: "中線", hint: "3d 動能 · 7d 突破 · 12h 加速" },
};

const REFRESH_MS = 15_000;

function hotnessColor(h: number): string {
  if (h >= 5) return "bg-red-500/20 text-red-300 border-red-500/40";
  if (h >= 3) return "bg-orange-500/20 text-orange-300 border-orange-500/40";
  if (h >= 1.5) return "bg-amber-500/20 text-amber-200 border-amber-500/40";
  if (h >= 0) return "bg-emerald-500/10 text-emerald-300 border-emerald-500/20";
  return "bg-zinc-800/50 text-zinc-500 border-zinc-800";
}

function venueColor(v: string): string {
  if (v === "binance") return "text-yellow-300";
  if (v === "dex") return "text-fuchsia-300";
  return "text-zinc-400";
}

function SignalBar({ label, value }: { label: string; value: number }) {
  const width = Math.min(100, Math.max(0, Math.abs(value) * 20));
  const positive = value >= 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-20 shrink-0 text-zinc-500">{label}</span>
      <div className="flex-1 h-1.5 rounded bg-zinc-800 overflow-hidden">
        <div
          className={positive ? "h-full bg-emerald-400/70" : "h-full bg-rose-400/70"}
          style={{ width: `${width}%` }}
        />
      </div>
      <span className="w-12 text-right tabular-nums text-zinc-400">{value.toFixed(2)}</span>
    </div>
  );
}

export function Dashboard() {
  const [tab, setTab] = useState<Tab>("hotspots");
  const [scale, setScale] = useState<Scale>("ultra");
  const [scores, setScores] = useState<HotspotScore[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<HotspotScore | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [recentAlerts, setRecentAlerts] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("kind", filter);
      params.set("scale", scale);
      const [hotRes, healthRes] = await Promise.all([
        fetch(`/api/hotspots?${params.toString()}`, { cache: "no-store" }),
        fetch(`/api/health`, { cache: "no-store" }),
      ]);
      if (!hotRes.ok) throw new Error(`HTTP ${hotRes.status}`);
      const body = (await hotRes.json()) as { scores: HotspotScore[]; ts: number };
      setScores(body.scores);
      setUpdatedAt(body.ts);
      if (healthRes.ok) {
        const h = (await healthRes.json()) as {
          env: { hourlyUsed?: number };
        };
        if (h.env.hourlyUsed != null) setRecentAlerts(h.env.hourlyUsed);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [filter, scale]);

  useEffect(() => {
    if (tab !== "hotspots") return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      void load();
      timerRef.current = setTimeout(tick, REFRESH_MS);
    };
    tick();
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [load, tab]);

  const visible = useMemo(() => scores.slice(0, 80), [scores]);

  const showOnchainMeta = filter === "onchain";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="sticky top-0 z-20 border-b border-zinc-800 bg-zinc-950/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Crypto 熱點偵測</h1>
            <p className="text-xs text-zinc-500">
              Binance 合約 / DexScreener / 公告 / 新聞 / Twitter
              <span className="ml-2 text-amber-400/80">
                [{SCALE_LABELS[scale].short}]
              </span>
              {updatedAt && (
                <span className="ml-2 text-zinc-600">
                  · 更新於 {new Date(updatedAt).toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <nav className="flex gap-1 text-xs items-center">
            {recentAlerts != null && (
              <span
                className="mr-3 px-2 py-1 rounded border border-zinc-800 text-zinc-400 text-[11px]"
                title="過去 60 分鐘推送的通知數（無硬上限）"
              >
                近一小時 {recentAlerts} 推送
              </span>
            )}
            <button
              type="button"
              onClick={() => setTab("hotspots")}
              className={tabButtonClass(tab === "hotspots")}
            >
              熱點榜
            </button>
            <button
              type="button"
              onClick={() => setTab("xtrends")}
              className={tabButtonClass(tab === "xtrends")}
            >
              X 流量
            </button>
            <a
              href="/ig"
              className="px-3 py-1.5 rounded-md border border-emerald-800/60 text-emerald-300 hover:text-emerald-200 hover:border-emerald-700"
            >
              IG
            </a>
            <a
              href="/settings"
              className="px-3 py-1.5 rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700"
            >
              設定
            </a>
          </nav>
        </div>
        {tab === "hotspots" && (
          <div className="max-w-7xl mx-auto px-6 pb-3 flex items-center justify-between gap-4 text-xs flex-wrap">
            <div className="flex gap-1">
              {(Object.keys(FILTER_LABELS) as Filter[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={
                    "px-3 py-1.5 rounded-md border transition-colors "
                    + (filter === f
                      ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                      : "border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700")
                  }
                >
                  {FILTER_LABELS[f]}
                </button>
              ))}
            </div>
            <div className="flex gap-1 items-center">
              <span className="text-[10px] uppercase text-zinc-500 mr-1">時間尺度</span>
              {(Object.keys(SCALE_LABELS) as Scale[]).map((sc) => (
                <button
                  key={sc}
                  type="button"
                  onClick={() => setScale(sc)}
                  title={SCALE_LABELS[sc].hint}
                  className={
                    "px-3 py-1.5 rounded-md border transition-colors "
                    + (scale === sc
                      ? "border-amber-500/60 bg-amber-500/10 text-amber-300"
                      : "border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700")
                  }
                >
                  {SCALE_LABELS[sc].short}
                  {sc === "ultra" && <span className="ml-1 text-[9px] text-zinc-500">通知</span>}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="px-3 py-1.5 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "更新中…" : "↻ 立即刷新"}
            </button>
          </div>
        )}
      </header>

      {error && tab === "hotspots" && (
        <div className="max-w-7xl mx-auto px-6 py-2 text-xs text-rose-400">
          載入失敗：{error}
        </div>
      )}

      {tab === "hotspots" ? (
        <main className="max-w-7xl mx-auto px-6 py-6">
          <div className="overflow-x-auto rounded-lg border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/80 text-xs text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">#</th>
                  <th className="px-4 py-3 text-left font-medium">代幣</th>
                  {showOnchainMeta ? (
                    <>
                      <th className="px-4 py-3 text-left font-medium">鏈 / 合約</th>
                      <th className="px-4 py-3 text-right font-medium">市值</th>
                      <th className="px-4 py-3 text-right font-medium">24h 量</th>
                      <th className="px-4 py-3 text-right font-medium">流動性</th>
                    </>
                  ) : (
                    <th className="px-4 py-3 text-left font-medium">類型</th>
                  )}
                  <th className="px-4 py-3 text-right font-medium">Hotness</th>
                  <th className="px-4 py-3 text-left font-medium">信號拆解</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-900">
                {visible.map((s, i) => (
                  <tr
                    key={s.tokenId}
                    onClick={() => setSelected(s)}
                    className="hover:bg-zinc-900/60 cursor-pointer"
                  >
                    <td className="px-4 py-3 text-zinc-500 tabular-nums w-10">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{s.symbol}</span>
                        <span className={`text-[10px] uppercase ${venueColor(s.venue)}`}>
                          {s.venue}
                        </span>
                      </div>
                    </td>
                    {showOnchainMeta ? (
                      <>
                        <td className="px-4 py-3 text-xs">
                          <div className="text-zinc-300">{s.chain ?? "—"}</div>
                          {s.address && <AddressChip address={s.address} chain={s.chain} />}
                        </td>
                        <td className="px-4 py-3 text-right text-xs tabular-nums">
                          {formatMoney(s.marketCap ?? s.fdv)}
                        </td>
                        <td className="px-4 py-3 text-right text-xs tabular-nums">
                          {formatMoney(s.volume24h)}
                        </td>
                        <td className="px-4 py-3 text-right text-xs tabular-nums">
                          {formatMoney(s.liquidityUsd)}
                        </td>
                      </>
                    ) : (
                      <td className="px-4 py-3 text-xs text-zinc-400">{s.kind}</td>
                    )}
                    <td className="px-4 py-3 text-right w-28">
                      <span
                        className={`inline-block px-2 py-0.5 rounded border text-xs tabular-nums ${hotnessColor(s.hotness)}`}
                      >
                        {s.hotness.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-x-5 gap-y-1">
                        <MiniSignal label="動能" value={s.breakdown.priceMomentum} />
                        <MiniSignal label="量" value={s.breakdown.volumeSpike} />
                        <MiniSignal label="突破" value={s.breakdown.breakout} highlight />
                        <MiniSignal label="加速" value={s.breakdown.velocity} highlight />
                        <MiniSignal label="OI" value={s.breakdown.oiGrowth} highlight />
                        <MiniSignal label="消息" value={s.breakdown.news} />
                        <MiniSignal label="社群" value={s.breakdown.socialBuzz} />
                        <MiniSignal label="爆倉" value={s.breakdown.liquidations} />
                        <MiniSignal label="資金費" value={s.breakdown.fundingBias} />
                      </div>
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={showOnchainMeta ? 8 : 5} className="px-4 py-12 text-center text-zinc-600">
                      還在收集資料… 初次啟動需 30–60 秒累積足夠樣本。
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </main>
      ) : (
        <XTrends />
      )}

      {selected && (
        <TokenDrawer
          score={selected}
          onClose={() => setSelected(null)}
          SignalBar={SignalBar}
        />
      )}
    </div>
  );
}

function tabButtonClass(active: boolean): string {
  return (
    "px-3 py-1.5 rounded-md border transition-colors "
    + (active
      ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
      : "border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700")
  );
}

function formatMoney(v: number | undefined | null): string {
  if (v == null || !Number.isFinite(v) || v <= 0) return "—";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

const EXPLORER: Record<string, (addr: string) => string> = {
  ethereum: (a) => `https://etherscan.io/token/${a}`,
  eth: (a) => `https://etherscan.io/token/${a}`,
  bsc: (a) => `https://bscscan.com/token/${a}`,
  base: (a) => `https://basescan.org/token/${a}`,
  arbitrum: (a) => `https://arbiscan.io/token/${a}`,
  polygon: (a) => `https://polygonscan.com/token/${a}`,
  polygon_pos: (a) => `https://polygonscan.com/token/${a}`,
  avalanche: (a) => `https://snowtrace.io/token/${a}`,
  solana: (a) => `https://solscan.io/token/${a}`,
  sui: (a) => `https://suiscan.xyz/mainnet/object/${a}`,
  ton: (a) => `https://tonscan.org/address/${a}`,
  hyperevm: (a) => `https://hyperliquid.xyz/address/${a}`,
  hyperliquid: (a) => `https://hyperliquid.xyz/address/${a}`,
};

function explorerUrl(chain: string | null | undefined, address: string): string | null {
  if (!chain) return null;
  const fn = EXPLORER[chain.toLowerCase()];
  return fn ? fn(address) : null;
}

function AddressChip({ address, chain }: { address: string; chain: string | null | undefined }) {
  const [copied, setCopied] = useState<"ok" | "err" | null>(null);
  const short = `${address.slice(0, 6)}…${address.slice(-4)}`;
  const url = explorerUrl(chain, address);
  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await copyText(address);
    setCopied(ok ? "ok" : "err");
    setTimeout(() => setCopied(null), 1500);
  };
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-500 mt-0.5">
      <button
        type="button"
        onClick={copy}
        className={
          copied === "ok"
            ? "text-emerald-300"
            : copied === "err"
              ? "text-rose-400"
              : "hover:text-emerald-300"
        }
        title="複製合約地址"
      >
        {copied === "ok" ? "✓ 已複製" : copied === "err" ? "✗ 失敗" : short}
      </button>
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-zinc-600 hover:text-zinc-300"
          title="瀏覽器查看"
        >
          ↗
        </a>
      )}
    </span>
  );
}

function MiniSignal({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  const strong = highlight && value >= 1.5;
  const color = strong
    ? "text-amber-300 font-semibold"
    : value >= 1.5
      ? "text-emerald-300"
      : value <= -1
        ? "text-rose-400"
        : "text-zinc-500";
  return (
    <div className="flex items-baseline gap-1 text-[11px]">
      <span className="text-zinc-500">{label}</span>
      <span className={`tabular-nums ${color}`}>{value.toFixed(2)}</span>
    </div>
  );
}
